import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
  model,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { disabled, form, FormField, type FormValueControl } from '@angular/forms/signals';
import {
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { IconsInitConfigType } from '../../../constants';
import { SelectOptionType, SelectSizeType } from '../../../types';
import { FlagIconUIComponent } from '../../atoms/flag-icon/flag-icon.component';
import { IconUIComponent } from '../../atoms/icon/icon.component';
import { UiFieldActionDirective } from '../../../directives';
import { ValidationErrorUIComponent } from '../../atoms/validation-error/validation-error.component';
import { SelectMultiValuePipe } from './pipes/select-multi-value.pipe';
import { SelectShowOptionCheckmarkPipe } from './pipes/select-show-option-checkmark.pipe';

export type SelectFieldValue = SelectOptionType['value'] | SelectOptionType['value'][];

@Component({
  selector: 'fr8bot-ui-select-field',
  imports: [
    NgMultiLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgSelectComponent,
    FormField,
    FlagIconUIComponent,
    IconUIComponent,
    SelectMultiValuePipe,
    SelectShowOptionCheckmarkPipe,
    TranslatePipe,
    UiFieldActionDirective,
    ValidationErrorUIComponent,
  ],
  templateUrl: './select-field.component.html',
  styleUrl: './select-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectFieldComponent
  extends UiFieldControl
  implements FormValueControl<SelectFieldValue>
{
  readonly value = model<SelectFieldValue>(null);

  readonly label = input<string | null>(null);
  readonly size = input<SelectSizeType>('medium');
  readonly multiple = input(false);
  readonly prefixIcon = input<IconsInitConfigType | null>(null);
  readonly showCheckmark = input(false);
  readonly placeholder = input('common.selectComponent.all');
  readonly options = input<SelectOptionType[]>([]);
  readonly canClear = input(false);

  protected readonly errorRef = contentChild(ValidationErrorUIComponent);
  protected readonly isOpen = signal(false);
  protected readonly selectedFlag = computed(() =>
    this.multiple()
      ? undefined
      : this.options().find((option) => option.value === this.value())?.flag,
  );

  // ng-select only speaks ControlValueAccessor; a local field over the same model drives it.
  protected readonly selectField = form(this.value, (path) => {
    disabled(path, { when: () => this.disabled() });
  });

  protected clearState(): void {
    this.value.set(null);
  }
}
